// L2 — Captura hora-línea (F0-6). La pantalla más usada del MVP (80% del
// tiempo del Líder).
//
// Layout:
//   1. Header fijo: nombre líder, línea, turno, hora actual, progreso del turno
//   2. Form: SKU, cantidad, operarios, golpes/min (+ "No aplica"), OP, paros
//   3. Modal de paro (multi-instancia, FIFO add)
//   4. Colapsable "Mis horas del turno" (polling 30s)
//   5. Colapsable "% cumplimiento del día" (estimación simple en MVP)

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import type {
  BloqueTurno,
  CausalParoTreeNode,
  ContextoLider,
  LiderAsignacionHoy,
  ParoAbierto,
  ParoCerradoPorMtto,
  RegistroBloqueCreate,
  RegistroProduccionHora,
  RegistroTramoCreate,
  SkuConCapacidad,
} from 'schemas';
import {
  avanzarHoraHHMM,
  bloqueQueContieneHora,
  coberturaParosBloque,
  combinarFechaHoraTurno,
  combinarHoraCierreParo,
  generarBloquesTurno,
  isHoraEnVentanaCaptura,
  isHoraInsideTurno,
  registroTramoCreateSchema,
} from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Collapsible } from '../../components/ui/collapsible';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LiderLayout } from '../../components/layout/LiderLayout';
import { Separator } from '../../components/ui/separator';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../lib/auth';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { limpiarEntradaLote, propsInputLote, validarLoteUi } from '../../lib/formatoLote';
import { cn } from '../../lib/utils';
import { unidadDeSku } from '../../lib/unidades';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import { ApiError } from '../../lib/api/client';
import {
  useCausalesParo,
  useCerrarParo,
  useContextoLider,
  useCrearRegistro,
  useParosAbiertos,
  useParosCerradosPorMtto,
  useRegistrosTurnoHoy,
  useSkusByLinea,
} from '../../lib/api/queries';
import {
  agruparPorCelda,
  CantidadConMetaCell,
  CantidadInput,
  cantidadSkusDistintos,
  cantidadTotalGrupo,
  cumplimientoGrupoPct,
  CumplimientoBloqueCell,
  duracionBloqueMin,
  detalleRegistroGuardado,
  formatearMiles,
} from './shared';
import { Badge } from '../../components/ui/badge';
import { RegistroAccionesLider } from './RegistroAccionesLider';
import { BarraTramos, type SegmentoTramo, validarNuevoTramo } from './BarraTramos';
import { EstadoTurnoCard } from './EstadoTurnoCard';
import { CausalPicker, causalEspecificaDe } from './CausalPicker';
import { DatosMaquinaLider } from './DatosMaquinaLider';
import { programacionDeLinea } from './programacion-linea';
import { FuenteBadge } from '../../components/ui/fuente-badge';

// =============================================================================
// Form schema (subset del Zod compartido — el resto lo deriva el handler)
// =============================================================================

// Minutos dentro de la hora en curso durante los que el form sugiere por
// defecto capturar la hora ANTERIOR (la que acaba de cerrar) en vez de la hora
// en curso. La producción se cuenta al terminar la hora → apenas cierra, lo que
// el líder tiene en mano es la de esa hora. Apuntar al bloque que terminó evita
// el reproceso por cargarla en el bloque equivocado. Pasado el margen, el
// default vuelve a la hora en curso. Es captura NORMAL (en turno), no back-fill.
const VENTANA_SUGERIR_HORA_ANTERIOR_MIN = 30;

const formParoSchema = z.object({
  causal_madre_id: z.string().uuid('Elija una categoría'),
  causal_id: z.string().uuid('Elija una causal'),
  hora_inicio: z.string().min(1, 'Requerido'),
  hora_fin: z.string().nullable(),
  observaciones: z.string().nullable(),
  requiere_observacion: z.boolean().default(false),
});
type FormParo = z.infer<typeof formParoSchema>;

// Form values: nullable para forzar que el líder TOQUE cada campo. El schema
// final del payload (registroProduccionHoraCreate) sí pide non-null; estos
// campos opcionales-en-form se validan en onSubmit antes del POST.
//
// `paros_estado` no va al payload — es solo un gate UX: el líder DEBE declarar
// explícitamente si hubo o no paros en este bloque. Si dice "Sí", paros≥1; si
// dice "No", paros=[].
interface FormValues {
  sku_id: string;
  cantidad_procesada: number | null;
  numero_operarios: number | null;
  golpes_por_minuto: number | null;
  op_lote_oc_cliente: string;
  // Tramos AGREGADOS más allá del principal (multi-sku-por-hora, Lote 4). El
  // tramo principal (índice 0, campos sueltos arriba) es el que hoy vive
  // inline en la Card — con este array vacío la UI es idéntica a hoy (regla
  // de oro). Cada item ya viene VALIDADO por `registroTramoCreateSchema` en
  // el modal `AgregarTramoModal` antes de entrar acá — no hace falta
  // re-validar sus campos al armar el payload.
  tramosExtra: RegistroTramoCreate[];
  paros: FormParo[];
  paros_estado: 'si' | 'no' | null;
  // Motivo obligatorio (≥20) cuando la captura es fuera de turno (back-fill de
  // un bloque saltado o flag fueraTurno). Vacío en la captura normal en turno.
  motivo_fuera_turno: string;
}

// =============================================================================
// Componente principal
// =============================================================================

export function L2CapturaPage() {
  const { lineaId } = useParams<{ lineaId: string }>();
  const [searchParams] = useSearchParams();
  const fueraTurnoFlag = searchParams.get('fueraTurno') === '1';
  const razonFueraTurnoQuery = searchParams.get('razon') ?? '';
  const { state, signOut } = useAuth();

  const contextoQ = useContextoLider();
  const skusQ = useSkusByLinea(lineaId);
  const causalesQ = useCausalesParo();
  const crearMut = useCrearRegistro();

  // 1 min clock (para header + barra de progreso)
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (contextoQ.isLoading || skusQ.isLoading || causalesQ.isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando captura…</p>
      </main>
    );
  }

  if (contextoQ.isError || !contextoQ.data) {
    return <ErrorState message="No pudimos cargar su contexto" onSignOut={signOut} />;
  }

  // Si la carga de SKUs falló (ej. 500), NO mostrar el form con "sin SKUs"
  // (mensaje engañoso): mostrar el error real para no esconder un bug de API.
  if (skusQ.isError) {
    return <ErrorState message="No pudimos cargar los SKUs de esta línea" onSignOut={signOut} />;
  }

  // Buscar la programación correspondiente a esta línea
  const programacion: LiderAsignacionHoy | undefined = programacionDeLinea(
    contextoQ.data.programaciones_hoy,
    lineaId,
    contextoQ.data.turno_actual_id,
  );

  if (!programacion) {
    // Antes de asumir un error genérico: si el día no opera o el turno arranca
    // dentro de las próximas 12h, mostrar el estado específico (design
    // horario-turno-dia §6) en vez de "línea no programada" — con `no_opera`
    // esta línea nunca aparece en `programaciones_hoy` (backend la filtra).
    const { proximo_inicio, dia_sin_turno } = contextoQ.data;
    if (proximo_inicio || dia_sin_turno) {
      return (
        <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
          <EstadoTurnoCard
            proximoInicio={proximo_inicio}
            diaSinTurno={dia_sin_turno}
            ahoraLocal={`${contextoQ.data.fecha_hoy}T${contextoQ.data.hora_servidor}`}
          />
          <button onClick={signOut} className="text-xs text-fg-tertiary underline">
            Cerrar sesión
          </button>
        </main>
      );
    }
    return (
      <ErrorState message="Esta línea no está programada para usted hoy" onSignOut={signOut} />
    );
  }

  return (
    <CapturaForm
      contexto={contextoQ.data}
      contextoActualizadoAt={contextoQ.dataUpdatedAt}
      programacion={programacion}
      skus={skusQ.data ?? []}
      causales={causalesQ.data ?? []}
      currentUserId={state.claims.sub}
      now={now}
      fueraTurnoFlag={fueraTurnoFlag}
      razonFueraTurnoInicial={razonFueraTurnoQuery}
      onSubmit={async (values, opts) => {
        try {
          await crearMut.mutateAsync(values);
          const totalUnidades = values.tramos.reduce((acc, t) => acc + t.cantidad_procesada, 0);
          toast.success('Registro guardado', {
            description: detalleRegistroGuardado(opts.skuLabel, totalUnidades),
          });
          opts.resetForm();
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.status === 403) {
              toast.error('No está programado en esta línea hoy', { description: err.message });
            } else if (err.status === 409) {
              toast.error('Ya existe un registro para esta hora', { description: err.message });
            } else if (err.status === 423) {
              toast.error('Fuera de la ventana de captura', {
                description: 'Pídale al supervisor que cargue este bloque.',
              });
            } else if (err.code === 'bloque_no_vigente') {
              toast.error('La hora del bloque ya no es la actual', {
                description: err.message,
              });
            } else {
              toast.error('Error al guardar', { description: err.message });
            }
          } else {
            toast.error('Error al guardar', {
              description: err instanceof Error ? err.message : 'unknown',
            });
          }
        }
      }}
      isSubmitting={crearMut.isPending}
    />
  );
}

// =============================================================================
// CapturaForm — UI + estado del form (RHF)
// =============================================================================

interface CapturaFormProps {
  contexto: ContextoLider;
  /** Epoch ms del fetch del contexto (dataUpdatedAt de TanStack Query). */
  contextoActualizadoAt: number;
  programacion: LiderAsignacionHoy;
  skus: SkuConCapacidad[];
  causales: CausalParoTreeNode[];
  currentUserId: string;
  now: Date;
  fueraTurnoFlag: boolean;
  razonFueraTurnoInicial: string;
  isSubmitting: boolean;
  onSubmit: (
    payload: RegistroBloqueCreate,
    opts: { skuLabel: string; resetForm: () => void },
  ) => Promise<void>;
}

export function CapturaForm({
  contexto,
  contextoActualizadoAt,
  programacion,
  skus,
  causales,
  currentUserId,
  now,
  fueraTurnoFlag,
  razonFueraTurnoInicial,
  isSubmitting,
  onSubmit,
}: CapturaFormProps) {
  // Fuente de verdad temporal: SERVIDOR (tenant tz), no browser.
  // `contexto.hora_servidor` viene del PG con SET LOCAL TimeZone aplicado, pero
  // es un snapshot del momento del fetch: el contexto NO se re-fetchea solo
  // (sin refetchInterval y refetchOnWindowFocus=false global). Se AVANZA
  // localmente con el delta transcurrido en el dispositivo (elapsed monotónico
  // — no la hora absoluta del browser, que puede estar en otra tz). Sin esto,
  // una PWA abierta toda la noche etiquetaba el bloque con la hora del último
  // fetch (T3 Vertical 1, 22-jul-2026: la captura de 01:00 entró como un
  // segundo 00:00). `now` tickea cada 60s y arrastra este recálculo.
  const horaServidor = avanzarHoraHHMM(
    contexto.hora_servidor,
    now.getTime() - contextoActualizadoAt,
  ); // "HH:MM" zona tenant
  const minutoActual = Number(horaServidor.slice(3, 5)); // MM de la hora en curso
  const { atribucionCruce, formatoLote } = useTenantConfig();
  const inputLote = propsInputLote(formatoLote);
  // Fecha OPERATIVA de esta programación (no contexto.fecha_hoy): el backend la
  // deriva según la política del tenant (dia_inicio: el día en que arrancó el
  // turno; dia_fin: el día en que termina). Los timestamps físicos de paros se
  // componen abajo con la MISMA política para que coincidan con la ventana.
  const fechaHoy = programacion.fecha_operativa; // YYYY-MM-DD zona tenant (fecha operativa)

  // Ventana de captura = turno + gracia post-cierre. `dentroTurnoEstricto` = el
  // turno corriendo ahora; `enGraciaCierre` = los ~60 min tras hora_fin para
  // grabar el último bloque sin caer en "fuera de turno".
  const dentroTurnoEstricto = isHoraInsideTurno(horaServidor, programacion.turno);
  const dentroTurno = isHoraEnVentanaCaptura(horaServidor, programacion.turno);
  const enGraciaCierre = dentroTurno && !dentroTurnoEstricto;
  const esFueraTurno = !dentroTurno || fueraTurnoFlag;
  // Bloque "actual" a capturar: el que CONTIENE la hora del servidor
  // (contención por rangos, NUNCA HH:00/date_trunc — un turno 13:30→21:00 a
  // las 13:35 vive en el bloque 13:30–14:00, no en uno 13:00–14:00). En la
  // gracia post-cierre (el reloj ya pasó hora_fin y no cae en ningún bloque)
  // el bloque a grabar es el ÚLTIMO del turno.
  const bloquesTurno = generarBloquesTurno(programacion.turno);
  const bloqueVigente = bloqueQueContieneHora(bloquesTurno, horaServidor);
  const horaInicio =
    bloqueVigente?.inicio ??
    (enGraciaCierre
      ? (bloquesTurno[bloquesTurno.length - 1]?.inicio ?? horaServidor)
      : horaServidor);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      sku_id: '',
      cantidad_procesada: null,
      numero_operarios: null,
      golpes_por_minuto: null,
      op_lote_oc_cliente: '',
      tramosExtra: [],
      paros: [],
      paros_estado: null,
      motivo_fuera_turno: razonFueraTurnoInicial,
    },
  });

  const {
    fields: paros,
    append: appendParo,
    remove: removeParo,
  } = useFieldArray({
    control,
    name: 'paros',
  });

  // Tramos agregados (multi-sku-por-hora, Lote 4) — el tramo principal
  // (índice 0) son los campos sueltos de siempre; estos son los que el
  // líder agrega vía el modal "+ Agregar SKU" cuando 2+ SKUs corrieron en
  // la misma celda.
  const {
    fields: tramosExtra,
    append: appendTramoExtra,
    remove: removeTramoExtra,
  } = useFieldArray({
    control,
    name: 'tramosExtra',
  });

  const selectedSkuId = watch('sku_id');
  const selectedSku = skus.find((s) => s.id === selectedSkuId) ?? null;
  // Unidad por SKU (GRANEL → kg). Los demás SKUs siguen como siempre.
  const unidadDeSkuId = (skuId: string) => unidadDeSku(skus.find((s) => s.id === skuId));

  // Auto-fill operarios estándar al elegir SKU (UX nicety). Solo si el campo
  // está vacío — no pisa edición manual.
  useEffect(() => {
    if (selectedSku?.personas_estandar_turno && watch('numero_operarios') === null) {
      setValue('numero_operarios', selectedSku.personas_estandar_turno);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watch() no es dep
  }, [selectedSku, setValue]);

  // Si el SKU activo NO usa golpes/min, forzar el campo a null (evita valor
  // fantasma de un SKU anterior). El backend lo rechazaría con
  // golpes_not_applicable.
  useEffect(() => {
    if (selectedSku && !selectedSku.golpes_min_aplica) {
      setValue('golpes_por_minuto', null);
    }
  }, [selectedSku, setValue]);

  const [paroModalOpen, setParoModalOpen] = useState(false);
  const [agregarTramoOpen, setAgregarTramoOpen] = useState(false);

  // Confirmación de producción 0: payload listo esperando el "sí, fue 0"
  // explícito del líder (doble seguro junto al check de paro total). `celda`
  // es el rango del bloque (no viaja en `RegistroBloqueCreate`, que solo
  // tiene rangos POR TRAMO) — se guarda para mostrarlo en el diálogo.
  const [confirmarCero, setConfirmarCero] = useState<{
    payload: RegistroBloqueCreate;
    opts: { skuLabel: string; resetForm: () => void };
    celda: { inicio: string; fin: string };
  } | null>(null);

  // Producción 0 ⇒ la máquina no corrió ⇒ golpes/min se fuerza a null y el
  // input se bloquea (el backend rechaza golpes con cantidad 0).
  const cantidadActual = watch('cantidad_procesada');
  useEffect(() => {
    if (cantidadActual === 0) {
      setValue('golpes_por_minuto', null);
    }
  }, [cantidadActual, setValue]);

  // Back-fill: bloque saltado del turno que el líder elige re-capturar (HH:MM).
  // null = captura normal del bloque actual. Cuando está set, el form apunta a
  // ese bloque pasado, fuerza fuera-de-turno y exige motivo.
  const [backfillBloque, setBackfillBloque] = useState<string | null>(null);

  // Mis horas del turno (polling 30 s; 10 s si la línea está conectada, R12)
  const misHoras = useRegistrosTurnoHoy(programacion.turno.id, programacion.linea.fuente_captura);

  // Agrupación por celda (multi-sku-por-hora, Lote 5): una celda con 2+
  // tramos se muestra como 1 card con badge "N SKUs" + sub-filas; con 1 solo
  // registro el grupo tiene 1 tramo y se renderiza IDÉNTICO a hoy. Reusa
  // `bloqueQueContieneHora` (misma fuente de verdad que el gate del backend),
  // así que datos históricos sin partición real (rango completo duplicado,
  // ej. 22-jul) se agrupan sin romper — el prorrateo usa la duración PROPIA
  // de cada tramo, nunca la de la celda.
  const gruposMisHoras = useMemo(
    () => agruparPorCelda(misHoras.data ?? [], generarBloquesTurno(programacion.turno)),
    [misHoras.data, programacion.turno],
  );

  // Bloque efectivo a capturar: el de back-fill si se eligió uno, si no el del
  // reloj del servidor. El back-fill SIEMPRE es captura fuera de turno. El fin
  // sale SIEMPRE del objeto `BloqueTurno` real (`.fin`), nunca de +1h local.
  // Sugerencia "hora que terminó" (ver VENTANA_SUGERIR_HORA_ANTERIOR_MIN):
  // en los primeros 30 min de la hora, si la hora anterior es un bloque del
  // turno y todavía no se capturó, el form apunta a ella por defecto. El líder
  // puede saltar a la hora en curso con un toque (setForzarHoraEnCurso).
  const [forzarHoraEnCurso, setForzarHoraEnCurso] = useState(false);
  const capturadosSet = useMemo(
    () => new Set((misHoras.data ?? []).map((r) => String(r.hora_inicio_bloque).slice(0, 5))),
    [misHoras.data],
  );
  const horaAnteriorTurno = useMemo(() => {
    const todos = generarBloquesTurno(programacion.turno);
    const idx = todos.findIndex((b) => b.inicio === horaInicio);
    return idx > 0 ? (todos[idx - 1]?.inicio ?? null) : null;
  }, [programacion.turno, horaInicio]);
  const usandoHoraAnterior =
    !forzarHoraEnCurso &&
    !esFueraTurno &&
    !fueraTurnoFlag &&
    !enGraciaCierre &&
    backfillBloque === null &&
    minutoActual < VENTANA_SUGERIR_HORA_ANTERIOR_MIN &&
    horaAnteriorTurno !== null &&
    !capturadosSet.has(horaAnteriorTurno) &&
    !capturadosSet.has(horaInicio);

  const bloqueActivo = backfillBloque ?? (usandoHoraAnterior ? horaAnteriorTurno! : horaInicio);
  const horaFinActiva = bloquesTurno.find((b) => b.inicio === bloqueActivo)?.fin ?? bloqueActivo;
  const esFueraTurnoEfectivo = backfillBloque !== null || esFueraTurno;

  // ===========================================================================
  // Multi-sku-por-hora (Lote 4): el tramo principal (índice 0, campos sueltos
  // arriba) SIEMPRE arranca en `bloqueActivo`. Su `hasta` es DERIVADO, no
  // guardado en el form: si hay tramos agregados, termina donde empieza el
  // PRIMERO de ellos (partición contigua por construcción); si no hay
  // ninguno, cubre toda la celda (`horaFinActiva`) — caso mono-SKU, idéntico
  // a hoy. Los tramos agregados SÍ guardan su rango explícito (fijado al
  // agregarlos vía el modal, sin auto-ajuste — un hueco entre el final del
  // tramo principal y el inicio del siguiente es válido transitoriamente, lo
  // detecta el chip de minutos asignados).
  const hayTramosExtra = tramosExtra.length > 0;
  const tramo0Hasta = tramosExtra[0]?.hora_inicio_bloque ?? horaFinActiva;
  const skuLabelPrincipal = selectedSku?.codigo_cliente ?? 'SKU 1';
  const segmentosTramos: SegmentoTramo[] = [
    { hora_inicio_bloque: bloqueActivo, hora_fin_bloque: tramo0Hasta, skuLabel: skuLabelPrincipal },
    ...tramosExtra.map((t) => ({
      hora_inicio_bloque: t.hora_inicio_bloque,
      hora_fin_bloque: t.hora_fin_bloque,
      skuLabel: skus.find((s) => s.id === t.sku_id)?.codigo_cliente ?? 'SKU',
    })),
  ];
  const celdaDuracionMin = duracionBloqueMin({
    hora_inicio_bloque: bloqueActivo,
    hora_fin_bloque: horaFinActiva,
  });
  const asignadoMin = segmentosTramos.reduce((acc, s) => acc + duracionBloqueMin(s), 0);
  const particionCompleta = asignadoMin >= celdaDuracionMin - 0.01;
  const skuIdsSegmentos = [
    selectedSku?.id ?? '__sku_principal_pendiente__',
    ...tramosExtra.map((t) => t.sku_id),
  ];
  const cantidadSkusSegmentos = new Set(skuIdsSegmentos).size;
  // Prefill del modal "Agregar tramo": desde = hasta del ÚLTIMO tramo actual
  // (el principal si todavía no se agregó ninguno, si no el último agregado);
  // hasta = fin de la celda (Req. UI, escenario "Agregar tramo vía modal").
  const desdeModal =
    tramosExtra.length > 0
      ? tramosExtra[tramosExtra.length - 1]!.hora_fin_bloque
      : avanzarHoraHHMM(bloqueActivo, Math.floor(celdaDuracionMin / 2) * 60_000);

  // Bloques saltados del turno = esperados − capturados, solo los ANTERIORES al
  // bloque actual del reloj (idxActual). Fuera del turno (idxActual === -1) se
  // listan todos los no capturados. Usa el helper compartido FE↔BE. Devuelve
  // los objetos `BloqueTurno` completos (no strings) para mostrar/enviar su
  // `.fin` real sin recalcularlo.
  const bloquesFaltantes: BloqueTurno[] = useMemo(() => {
    const todos = generarBloquesTurno(programacion.turno);
    const capturados = new Set(
      (misHoras.data ?? []).map((r) => String(r.hora_inicio_bloque).slice(0, 5)),
    );
    const idxActual = todos.findIndex((b) => b.inicio === horaInicio);
    return todos.filter((b, i) => !capturados.has(b.inicio) && (idxActual === -1 || i < idxActual));
  }, [misHoras.data, programacion.turno, horaInicio]);

  // ¿Mostrar la sección de back-fill? Dentro del turno (gap del turno en curso),
  // o en la gracia post-turno si algún bloque capturado sigue editable (señal del
  // backend, sin recalcular tiempos en el FE). El backend es el gate duro (423).
  const mostrarBackfill =
    dentroTurno || (misHoras.data ?? []).some((r) => r.editable_por_lider === true);

  // Paros abiertos del turno (heredados de bloques anteriores)
  const parosAbiertosQ = useParosAbiertos(programacion.turno.id);
  const parosAbiertos = parosAbiertosQ.data ?? [];

  // Paros que mantenimiento cerró por el líder en este turno. Aviso informativo:
  // sin esto el paro desaparecía del banner de arriba y nadie le decía nada.
  const parosCerradosMttoQ = useParosCerradosPorMtto(programacion.turno.id);
  const [avisoMttoDescartado, setAvisoMttoDescartado] = useState(false);
  const parosCerradosMtto = avisoMttoDescartado ? [] : (parosCerradosMttoQ.data ?? []);

  // Estado del drill-down de "mis horas"
  const [detalleHora, setDetalleHora] = useState<RegistroProduccionHora | null>(null);

  // Estado del mini-modal de cierre de paro abierto
  const [paroACerrar, setParoACerrar] = useState<ParoAbierto | null>(null);

  // OP/Lote heredado del bloque anterior (mismo turno). Se sugiere como
  // pre-fill pero el líder puede sobreescribir. Frecuente: la misma OC dura
  // varios bloques contiguos.
  const opHeredada = useMemo<string | null>(() => {
    if (!misHoras.data || misHoras.data.length === 0) return null;
    // Último bloque cronológico con OP no vacía
    const ultimoConOp = [...misHoras.data]
      .sort((a, b) => String(a.hora_inicio_bloque).localeCompare(String(b.hora_inicio_bloque)))
      .reverse()
      .find((r) => r.op_lote_oc_cliente && r.op_lote_oc_cliente.length > 0);
    return ultimoConOp?.op_lote_oc_cliente ?? null;
  }, [misHoras.data]);

  // % cumplimiento del día — ponderado por la capacidad estándar de CADA SKU
  // corrido (no del SKU activo). Σ cantidad real / Σ capacidad estándar × 100,
  // saltando registros sin capacidad mapeada. Aparece apenas hay ≥1 captura.
  const cumplimientoPct = useMemo(() => {
    if (!misHoras.data || misHoras.data.length === 0) return null;
    let real = 0;
    let meta = 0;
    for (const r of misHoras.data) {
      const cap = r.capacidad_estandar_unidades_hora;
      if (cap === null || cap === 0) continue; // sin estándar → no entra a la meta
      real += Number(r.cantidad_procesada);
      meta += cap * (duracionBloqueMin(r) / 60); // INV-2: prorrateado por duración real
    }
    if (meta === 0) return null;
    return Math.round((real / meta) * 100);
  }, [misHoras.data]);

  function onValidSubmit(values: FormValues) {
    if (!selectedSku) {
      toast.error('Elija un SKU antes de guardar');
      return;
    }
    // Gate del toggle: "Sí" requiere ≥1 paro nuevo en este bloque
    if (values.paros_estado === 'si' && values.paros.length === 0) {
      toast.error('Agregue al menos un paro o cambie su respuesta a "No"');
      return;
    }
    if (values.paros_estado === null) {
      toast.error('Indique si hubo paros antes de guardar');
      return;
    }
    // Estos campos están validados por las rules de Controller, pero TS no lo
    // sabe — chequeo defensivo + narrowing. El 0 es válido (paro total).
    if (values.cantidad_procesada === null || values.cantidad_procesada < 0) {
      toast.error('Cantidad procesada inválida');
      return;
    }
    if (values.numero_operarios === null) {
      toast.error('Operarios inválido');
      return;
    }
    // Chip de minutos asignados (Req. UI, escenario "Chip bloquea Guardar"):
    // con 2+ tramos, la partición debe cubrir el 100% de la celda antes de
    // guardar. Con 1 solo tramo (hayTramosExtra=false) no aplica — idéntico a
    // hoy, el tramo principal siempre cubre toda la celda.
    if (hayTramosExtra && !particionCompleta) {
      toast.error('Complete la partición del bloque antes de guardar', {
        description: `Faltan ${Math.round(celdaDuracionMin - asignadoMin)} min por asignar de ${Math.round(celdaDuracionMin)}.`,
      });
      return;
    }

    // Captura fuera de turno (back-fill o flag): motivo ≥20 obligatorio. Se toma
    // del campo visible; si no, del query param (flujo legacy ?razon=).
    const razonEfectiva = (values.motivo_fuera_turno.trim() || razonFueraTurnoInicial).trim();
    if (esFueraTurnoEfectivo && razonEfectiva.length < 20) {
      toast.error('Indique el motivo de la captura fuera de turno (mínimo 20 caracteres)');
      return;
    }

    // Tramo principal (índice 0): campos sueltos de siempre + rango derivado
    // (bloqueActivo→tramo0Hasta). Con 0 tramos agregados, tramo0Hasta ===
    // horaFinActiva → idéntico al payload mono-SKU de hoy.
    const tramoPrincipal: RegistroTramoCreate = {
      hora_inicio_bloque: bloqueActivo,
      hora_fin_bloque: tramo0Hasta,
      sku_id: values.sku_id,
      cantidad_procesada: values.cantidad_procesada,
      cantidad_kg: null,
      golpes_por_minuto: values.golpes_por_minuto,
      numero_operarios: values.numero_operarios,
      op_lote_oc_cliente: values.op_lote_oc_cliente.trim(),
    };
    // Los tramos agregados ya fueron validados por `registroTramoCreateSchema`
    // en el modal — se envían tal cual entraron al array.
    const tramos: RegistroTramoCreate[] = [tramoPrincipal, ...values.tramosExtra];

    const paros = values.paros.map((p) => ({
      causal_madre_id: p.causal_madre_id,
      causal_id: p.causal_id,
      // Turno-aware: en la cola post-medianoche del T3 la hora pertenece al
      // día calendario siguiente (+1d) — hace monotónico un paro 23:50→00:10.
      hora_inicio: combinarFechaHoraTurno(
        fechaHoy,
        p.hora_inicio,
        programacion.turno,
        atribucionCruce,
      ),
      hora_fin: p.hora_fin
        ? combinarFechaHoraTurno(fechaHoy, p.hora_fin, programacion.turno, atribucionCruce)
        : null,
      observaciones: p.observaciones,
      requiere_observacion: p.requiere_observacion,
    }));

    // Compose payload completo desde el form + ctx + razón fuera turno.
    // fecha NO se calcula desde el browser tz — viene del backend (contexto.
    // fecha_hoy + derivado de contexto.hora_servidor) que respeta SET LOCAL
    // TimeZone del tenant. Cierre técnico (review responsable).
    const payload: RegistroBloqueCreate = {
      fecha: fechaHoy,
      turno_id: programacion.turno.id,
      linea_id: programacion.linea.id,
      es_captura_fuera_turno: esFueraTurnoEfectivo,
      razon_fuera_turno: esFueraTurnoEfectivo ? razonEfectiva : null,
      capturado_por_supervisor: false,
      razon_delegacion: null,
      lider_asignado_user_id: null,
      origen_solicitud: 'programa',
      programado: true,
      paros,
      tramos,
    };

    const opts = {
      skuLabel:
        tramos.length > 1
          ? `${new Set(tramos.map((t) => t.sku_id)).size} SKUs · ${tramos.length} tramos`
          : selectedSku.descripcion,
      resetForm: () => {
        setBackfillBloque(null);
        removeTramoExtra();
        reset({
          sku_id: '',
          cantidad_procesada: null,
          numero_operarios: null,
          golpes_por_minuto: null,
          op_lote_oc_cliente: '',
          tramosExtra: [],
          paros: [],
          paros_estado: null,
          motivo_fuera_turno: '',
        });
      },
    };

    // Producción 0: doble seguro, POR TRAMO (traslada la regla de "bloque" a
    // "tramo" — misma semántica que el refine del schema compartido). (1) Los
    // paros deben cubrir COMPLETO el rango de CADA tramo en 0 (se avisa ANTES
    // de enviar, con mensaje accionable). (2) Confirmación explícita en modal
    // antes de guardar.
    const tramosEnCero = tramos.filter((t) => t.cantidad_procesada === 0);
    if (tramosEnCero.length > 0) {
      for (const t of tramosEnCero) {
        const { minutosCubiertos, minutosBloque } = coberturaParosBloque({
          fecha: fechaHoy,
          hora_inicio_bloque: t.hora_inicio_bloque,
          hora_fin_bloque: t.hora_fin_bloque,
          paros,
        });
        if (minutosCubiertos < minutosBloque - 0.01) {
          toast.error('Producción 0 requiere paro total', {
            description: `El tramo ${t.hora_inicio_bloque}–${t.hora_fin_bloque} debe estar cubierto por paros (${Math.round(
              minutosBloque,
            )} min). Hoy cubre ${Math.round(minutosCubiertos)} min.`,
          });
          return;
        }
      }
      setConfirmarCero({ payload, opts, celda: { inicio: bloqueActivo, fin: horaFinActiva } });
      return;
    }

    void onSubmit(payload, opts);
  }

  // Calcular pendientes para mostrar en el header contextual. INV-2: las
  // "horas" son la SUMA de duracionMinutos/60 de los bloques reales, no un
  // conteo de bloques (un turno con parciales de 30' no vale lo mismo que uno
  // de 60'). Con turnos de bloques :00 puros el resultado es idéntico al
  // conteo anterior (cada bloque dura 60' ⇒ suma/60 === cantidad de bloques).
  const totalHorasTurno = bloquesTurno.reduce((acc, b) => acc + b.duracionMinutos, 0) / 60;
  const horasCapturadas = (misHoras.data ?? []).reduce(
    (acc, r) => acc + duracionBloqueMin(r) / 60,
    0,
  );
  const horasPendientes = Math.max(0, totalHorasTurno - horasCapturadas);

  // Fecha en formato "Jue 26 de may." — humano y compacto para mobile.
  const fechaLegible = formatearFechaCorta(fechaHoy);

  return (
    <LiderLayout>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-12">
        {/* Sub-header contextual: bloque + fecha + barra progreso + pendientes */}
        <header className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                {contexto.tenant.nombre} · Turno {programacion.turno.numero} · {fechaLegible}
              </p>
              <h1 className="mt-0.5 text-xl font-medium text-fg">
                {programacion.linea.nombre}{' '}
                <span className="text-fg-tertiary">· {horaServidor}</span>
              </h1>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {contexto.programaciones_hoy.length > 1 ? (
                <Link
                  to="/capturar"
                  className="inline-flex min-h-11 items-center rounded border border-border px-3 text-sm font-medium text-fg-secondary transition-colors hover:bg-surface-overlay"
                >
                  Cambiar línea
                </Link>
              ) : null}
              <Link
                to="/historial"
                className="inline-flex min-h-11 items-center rounded border border-border px-3 text-sm font-medium text-fg-secondary transition-colors hover:bg-surface-overlay"
              >
                Historial
              </Link>
            </div>
          </div>
          <BarraProgresoTurno
            turno={programacion.turno}
            now={now}
            horasCapturadas={horasCapturadas}
            totalHorasTurno={totalHorasTurno}
            horasPendientes={horasPendientes}
          />
          {backfillBloque ? (
            <div className="flex items-center justify-between gap-2 rounded bg-warn-soft px-2 py-1 text-xs text-warn">
              <span>
                ⏳ Capturando bloque saltado{' '}
                <strong>
                  {bloqueActivo}–{horaFinActiva}
                </strong>{' '}
                (fuera de turno)
              </span>
              <button
                type="button"
                className="shrink-0 font-medium underline"
                onClick={() => setBackfillBloque(null)}
              >
                Cancelar
              </button>
            </div>
          ) : esFueraTurno ? (
            <p className="rounded bg-warn-soft px-2 py-1 text-xs text-warn">
              ⚠ Captura fuera de turno{' '}
              {razonFueraTurnoInicial ? `· "${razonFueraTurnoInicial}"` : ''}
            </p>
          ) : usandoHoraAnterior ? (
            <div className="flex items-center justify-between gap-2 rounded bg-accent-soft/60 px-2 py-1 text-xs text-fg">
              <span>
                📋 Está registrando la hora que <strong>acaba de terminar</strong>:{' '}
                <strong>
                  {bloqueActivo}–{horaFinActiva}
                </strong>
              </span>
              <button
                type="button"
                className="shrink-0 font-medium underline"
                onClick={() => setForzarHoraEnCurso(true)}
              >
                Es la hora en curso
              </button>
            </div>
          ) : null}
        </header>

        {/* Banner: paros sin cerrar del turno (heredados de bloques anteriores).
            El líder NO los re-agrega — los cierra desde acá. */}
        {parosAbiertos.length > 0 ? (
          <BannerParosAbiertos paros={parosAbiertos} onCerrar={(p) => setParoACerrar(p)} />
        ) : null}

        {/* Aviso: mantenimiento cerró un paro que el líder dejó abierto */}
        {parosCerradosMtto.length > 0 ? (
          <BannerParosCerradosPorMtto
            paros={parosCerradosMtto}
            onDescartar={() => setAvisoMttoDescartado(true)}
          />
        ) : null}

        {/* Integración con máquina: paros sin causal y horas por completar de
            esta línea. En líneas manuales no renderiza nada. */}
        <DatosMaquinaLider
          lineaId={programacion.linea.id}
          causales={causales}
          fuenteCaptura={programacion.linea.fuente_captura}
        />

        {/* El botón "Guardar registro" es sticky al fondo (h-12 + bottom-2 = 56 px).
            Cada control del form reserva 64 px de scroll-margin inferior para que
            el foco o un scrollIntoView no lo deje debajo del botón (tableta). */}
        <form
          onSubmit={handleSubmit(onValidSubmit)}
          className="flex flex-col gap-4 [&_*]:scroll-mb-16"
          data-testid="captura-form"
          noValidate
        >
          <Card>
            <CardHeader>
              <CardTitle>
                Bloque {bloqueActivo}–{horaFinActiva}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {/* SKU — Combobox con búsqueda */}
              <Controller
                control={control}
                name="sku_id"
                rules={{ required: 'Elija un SKU' }}
                render={({ field }) => {
                  const opts: ComboboxOption[] = skus.map((s) => ({
                    value: s.id,
                    label: s.codigo_cliente,
                    description: `${s.descripcion}${s.formato ? ` · ${s.formato}` : ''}`,
                    searchText: `${s.codigo_cliente} ${s.descripcion} ${s.formato ?? ''}`,
                  }));
                  return (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="sku_id">SKU</Label>
                      <Combobox
                        id="sku_id"
                        options={opts}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Buscar por código o descripción…"
                        invalid={Boolean(errors.sku_id)}
                        emptyLabel="Sin coincidencias"
                      />
                      {errors.sku_id ? (
                        <p className="text-xs text-crit">{errors.sku_id.message as string}</p>
                      ) : null}
                      {selectedSku?.capacidad_estandar_unidades_hora ? (
                        <p className="text-xs text-fg-tertiary">
                          Capacidad estándar:{' '}
                          <strong>
                            {formatearMiles(selectedSku.capacidad_estandar_unidades_hora)}
                          </strong>
                          /h
                          {selectedSku.golpes_min_aplica &&
                          selectedSku.capacidad_estandar_golpes_min
                            ? ` · ${selectedSku.capacidad_estandar_golpes_min} golpes/min`
                            : ''}
                        </p>
                      ) : null}
                      {skus.length === 0 ? (
                        <p className="text-xs text-warn">
                          ⚠ No hay SKUs configurados para esta línea. Contacte al coordinador.
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              />

              {/* Cantidad procesada + Operarios — defaults null, todos obligatorios */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Controller
                  control={control}
                  name="cantidad_procesada"
                  rules={{
                    validate: (v) => {
                      if (v === null) return 'Cantidad procesada es obligatoria';
                      // 0 es válido: hora con paro total (pide confirmación).
                      return v >= 0 || 'No puede ser negativa';
                    },
                  }}
                  render={({ field }) => (
                    <CantidadInput
                      id="cantidad_procesada"
                      label={
                        unidadDeSku(selectedSku) === 'kg'
                          ? 'Cantidad procesada (kg)'
                          : 'Cantidad procesada'
                      }
                      placeholder="Ej. 1.250"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.cantidad_procesada?.message as string | undefined}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="numero_operarios"
                  rules={{
                    required: 'Operarios es obligatorio',
                    validate: (v) => (v !== null && v >= 1 && v <= 20) || 'Entre 1 y 20',
                  }}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="numero_operarios">Operarios (1–20)</Label>
                      <Input
                        id="numero_operarios"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={20}
                        step={1}
                        placeholder="Ej. 3"
                        aria-invalid={Boolean(errors.numero_operarios) || undefined}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      {errors.numero_operarios ? (
                        <p className="text-xs text-crit">
                          {errors.numero_operarios.message as string}
                        </p>
                      ) : null}
                    </div>
                  )}
                />
              </div>

              {/* Golpes/min — obligatorio si el SKU lo aplica, deshabilitado si no */}
              <Controller
                control={control}
                name="golpes_por_minuto"
                rules={{
                  validate: (v, values) => {
                    if (!selectedSku) return true;
                    // Producción 0 (paro total): golpes va null aunque el SKU
                    // lo exija — la máquina no corrió.
                    if (values.cantidad_procesada === 0) return true;
                    if (selectedSku.golpes_min_aplica && (v === null || v <= 0)) {
                      return 'Golpes/min es obligatorio para este SKU';
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  const noAplica = selectedSku ? !selectedSku.golpes_min_aplica : false;
                  const cantidadCero = cantidadActual === 0;
                  return (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="golpes_por_minuto">
                        Golpes/min
                        {cantidadCero
                          ? ' (no aplica con producción 0)'
                          : noAplica
                            ? ' (no aplica a este SKU)'
                            : ''}
                      </Label>
                      <Input
                        id="golpes_por_minuto"
                        type="number"
                        inputMode="decimal"
                        min={1}
                        step={0.1}
                        disabled={noAplica || cantidadCero}
                        placeholder={noAplica || cantidadCero ? 'No aplica' : 'Ej. 80'}
                        aria-invalid={Boolean(errors.golpes_por_minuto) || undefined}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      {errors.golpes_por_minuto ? (
                        <p className="text-xs text-crit">
                          {errors.golpes_por_minuto.message as string}
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              />

              {/* OP / Lote / OC Cliente — obligatorio + chip "heredar del bloque anterior" */}
              <Controller
                control={control}
                name="op_lote_oc_cliente"
                rules={{
                  required: 'OP / Lote es obligatorio',
                  // Formato del lote de la empresa (B7): el mismo que aplica
                  // el API. Default: 8 dígitos numéricos (OP de Alpina).
                  validate: (v) => validarLoteUi(v, formatoLote),
                }}
                render={({ field }) => {
                  const heredable = opHeredada && opHeredada !== field.value;
                  return (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="op_lote_oc_cliente">OP / Lote / OC Cliente</Label>
                      <Input
                        id="op_lote_oc_cliente"
                        type="text"
                        inputMode={inputLote.inputMode}
                        maxLength={inputLote.maxLength}
                        placeholder={inputLote.placeholder}
                        aria-invalid={Boolean(errors.op_lote_oc_cliente) || undefined}
                        value={field.value}
                        // Formato numérico fijo (default): solo dígitos, así el
                        // líder no escribe separadores que el backend rechazaría.
                        onChange={(e) =>
                          field.onChange(limpiarEntradaLote(e.target.value, formatoLote))
                        }
                      />
                      {heredable ? (
                        <button
                          type="button"
                          onClick={() => field.onChange(opHeredada)}
                          className="self-start rounded-full border border-border-strong bg-accent-soft px-2.5 py-1 text-xs text-accent transition-colors hover:bg-accent-soft/80"
                        >
                          ↻ Usar del bloque anterior: <strong>{opHeredada}</strong>
                        </button>
                      ) : null}
                      {errors.op_lote_oc_cliente ? (
                        <p className="text-xs text-crit">
                          {errors.op_lote_oc_cliente.message as string}
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              />

              {/* Multi-sku-por-hora (Lote 4): entrada para agregar 2+ SKUs a
                  esta celda. Con 0 tramos agregados (regla de oro) NO se
                  renderiza barra ni chip — solo este botón, idéntico costo
                  visual al de hoy salvo esta fila. */}
              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="text-sm font-medium text-fg">
                  {hayTramosExtra
                    ? `${cantidadSkusSegmentos} SKUs · ${segmentosTramos.length} tramos`
                    : 'SKU único en este bloque'}
                </span>
                <Button
                  type="button"
                  size="touch"
                  variant="outline"
                  onClick={() => setAgregarTramoOpen(true)}
                >
                  + Agregar SKU
                </Button>
              </div>

              {hayTramosExtra ? (
                <div className="flex flex-col gap-2">
                  <BarraTramos
                    celdaInicio={bloqueActivo}
                    celdaFin={horaFinActiva}
                    tramos={segmentosTramos}
                  />
                  <div
                    className={cn(
                      'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                      particionCompleta ? 'bg-ok-soft text-ok' : 'bg-warn-soft text-warn',
                    )}
                  >
                    {Math.round(asignadoMin)}/{Math.round(celdaDuracionMin)} min asignados
                  </div>
                  {tramosExtra.map((t, i) => {
                    const skuTramo = skus.find((s) => s.id === t.sku_id);
                    return (
                      <div
                        key={t.id}
                        className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
                      >
                        <div>
                          <p className="font-medium">
                            {skuTramo ? skuTramo.codigo_cliente : 'SKU'}
                          </p>
                          <p className="text-xs text-fg-tertiary">
                            {t.hora_inicio_bloque} → {t.hora_fin_bloque} ·{' '}
                            {formatearMiles(t.cantidad_procesada)}{' '}
                            {unidadDeSkuId(t.sku_id) === 'kg' ? 'kg' : 'u.'} · {t.numero_operarios}{' '}
                            operarios
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTramoExtra(i)}
                        >
                          ✕
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {/* Motivo de captura fuera de turno (back-fill o flag). Obligatorio
                  ≥20. Solo visible cuando la captura es fuera de turno. */}
              {esFueraTurnoEfectivo ? (
                <Controller
                  control={control}
                  name="motivo_fuera_turno"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="motivo_fuera_turno">
                        Motivo de la captura fuera de turno (mínimo 20 caracteres)
                      </Label>
                      <Textarea
                        id="motivo_fuera_turno"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Ej: olvidé cargar este bloque durante el turno por atención a la línea"
                      />
                      <p
                        className={cn(
                          'text-xs',
                          field.value.trim().length >= 20 ? 'text-ok' : 'text-fg-tertiary',
                        )}
                      >
                        {field.value.trim().length}/20 caracteres mínimos
                      </p>
                    </div>
                  )}
                />
              ) : null}
            </CardContent>
          </Card>

          {/* Paros en ESTE bloque — gate Sí/No obligatorio */}
          <Card>
            <CardHeader>
              <CardTitle>¿Hubo paros nuevos en este bloque?</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Controller
                control={control}
                name="paros_estado"
                rules={{
                  validate: (v) => v !== null || 'Indique Sí o No antes de guardar',
                }}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => field.onChange('si')}
                        className={cn(
                          'flex min-h-11 items-center justify-center rounded border px-3 py-2 text-sm font-medium transition-colors',
                          field.value === 'si'
                            ? 'border-accent bg-accent text-accent-ink'
                            : 'border-border bg-surface-elevated text-fg hover:bg-surface-overlay',
                        )}
                      >
                        Sí, hubo paros
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange('no');
                          // Limpiar paros si los había: el líder eligió "no".
                          // setValue('paros', []) es atomic, evita issues de
                          // reindexación al hacer N removeParo() seguidos.
                          setValue('paros', []);
                        }}
                        className={cn(
                          'flex min-h-11 items-center justify-center rounded border px-3 py-2 text-sm font-medium transition-colors',
                          field.value === 'no'
                            ? 'border-ok bg-ok text-white'
                            : 'border-border bg-surface-elevated text-fg hover:bg-surface-overlay',
                        )}
                      >
                        No, sin paros
                      </button>
                    </div>
                    {errors.paros_estado ? (
                      <p className="text-xs text-crit">{errors.paros_estado.message as string}</p>
                    ) : null}
                  </div>
                )}
              />

              {/* Si dijo "Sí" → lista de paros + botón agregar. Si "No" → nada. */}
              {watch('paros_estado') === 'si' ? (
                <div className="flex flex-col gap-2 border-t border-border pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-fg">
                      Paros agregados ({paros.length})
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setParoModalOpen(true)}
                    >
                      + Agregar paro
                    </Button>
                  </div>
                  {paros.length === 0 ? (
                    <p className="text-xs text-warn">
                      Agregue al menos un paro o cambie su respuesta a "No".
                    </p>
                  ) : (
                    paros.map((p, i) => {
                      const causalNombre = findCausalNombre(causales, p.causal_id);
                      return (
                        <div
                          key={p.id}
                          className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
                        >
                          <div>
                            <p className="font-medium">{causalNombre ?? 'Sin causal'}</p>
                            <p className="text-xs text-fg-tertiary">
                              {p.hora_inicio} → {p.hora_fin ?? 'abierto'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParo(i)}
                          >
                            Quitar
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Submit — con 2+ tramos, deshabilitado hasta partición completa
              (chip en warn) y el label anuncia cuántos SKUs se guardan. */}
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting || (hayTramosExtra && !particionCompleta)}
            className="sticky bottom-2 z-10"
          >
            {isSubmitting ? (
              <>
                <Spinner className="border-white/30 border-t-white" /> Guardando…
              </>
            ) : hayTramosExtra ? (
              `Guardar bloque · ${cantidadSkusSegmentos} SKUs · ${segmentosTramos.length} tramos`
            ) : (
              'Guardar registro'
            )}
          </Button>
        </form>

        <Separator />

        {/* Bloques saltados del turno — back-fill acotado. Solo dentro de la
            ventana (turno + gracia). Al elegir uno, el form de arriba apunta a
            ese bloque como captura fuera de turno (motivo obligatorio). */}
        {mostrarBackfill && bloquesFaltantes.length > 0 && !backfillBloque ? (
          <div className="flex flex-col gap-2 rounded border border-warn/40 bg-warn-soft/40 p-3">
            <p className="text-sm font-medium text-fg">Bloques sin capturar de este turno</p>
            <p className="text-xs text-fg-tertiary">
              Si se saltó una hora, puede cargarla ahora. Queda como captura fuera de turno y el
              supervisor la valida.
            </p>
            <div className="flex flex-wrap gap-2">
              {bloquesFaltantes.map((b) => (
                <button
                  key={b.inicio}
                  type="button"
                  onClick={() => {
                    setBackfillBloque(b.inicio);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="rounded border border-border bg-surface-elevated px-2.5 py-1 text-xs font-medium text-fg transition-colors hover:bg-surface-overlay"
                >
                  + {b.inicio}–{b.fin}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Mis horas del turno — tabla alineada + drill-down al detalle */}
        <Collapsible label={`Mis horas del turno (${misHoras.data?.length ?? 0})`}>
          {misHoras.isLoading ? (
            <p className="text-sm text-fg-tertiary">Cargando…</p>
          ) : misHoras.data && misHoras.data.length > 0 ? (
            <div className="overflow-hidden rounded border border-border">
              <table className="w-full text-sm">
                <thead className="bg-surface-overlay text-[11px] uppercase tracking-wide text-fg-tertiary">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Bloque</th>
                    <th className="px-3 py-2 text-right font-medium tabular-nums">Cant.</th>
                    <th className="px-3 py-2 text-right font-medium">Cumpl.</th>
                    <th className="px-3 py-2 text-right font-medium">Paros</th>
                    <th className="px-3 py-2 text-right font-medium">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {gruposMisHoras.map((g) =>
                    g.tramos.length === 1 ? (
                      // 1 solo tramo en la celda: fila IDÉNTICA a hoy, sin
                      // agrupación (regla de oro — no-regresión mono-SKU).
                      <tr
                        key={g.tramos[0]!.id}
                        className="cursor-pointer border-t border-border transition-colors hover:bg-surface-overlay"
                        onClick={() => setDetalleHora(g.tramos[0]!)}
                      >
                        <td className="px-3 py-2 font-medium">
                          {String(g.tramos[0]!.hora_inicio_bloque).slice(0, 5)}–
                          {String(g.tramos[0]!.hora_fin_bloque).slice(0, 5)}
                          <FuenteBadge
                            fuente={g.tramos[0]!.fuente}
                            pendienteCompletar={g.tramos[0]!.pendiente_completar}
                            className="ml-2 align-middle"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <CantidadConMetaCell
                            registro={g.tramos[0]!}
                            unidad={unidadDeSkuId(g.tramos[0]!.sku_id)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <CumplimientoBloqueCell registro={g.tramos[0]!} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(g.tramos[0]!.paros ?? []).length > 0 ? (
                            <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-warn-soft px-1.5 py-0.5 text-xs font-medium text-warn">
                              {(g.tramos[0]!.paros ?? []).length}
                            </span>
                          ) : (
                            <span className="text-xs text-fg-tertiary">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <RegistroAccionesLider
                            registro={g.tramos[0]!}
                            currentUserId={currentUserId}
                          />
                        </td>
                      </tr>
                    ) : (
                      // 2+ tramos en la misma celda: card agrupado (total +
                      // badge "N SKUs") con una sub-fila clickable por tramo.
                      <Fragment key={g.celda.inicio}>
                        <tr className="border-t border-border bg-surface-overlay/40">
                          <td className="px-3 py-2 font-medium">
                            <span className="inline-flex items-center gap-2">
                              {g.celda.inicio}–{g.celda.fin}
                              <Badge tone="info">
                                {cantidadSkusDistintos(g.tramos)} SKUs · {g.tramos.length} tramos
                              </Badge>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="tabular-nums">
                              {formatearMiles(cantidadTotalGrupo(g.tramos))}
                              {g.tramos.every((t) => unidadDeSkuId(t.sku_id) === 'kg') ? ' kg' : ''}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const pct = cumplimientoGrupoPct(g.tramos);
                              return pct !== null ? (
                                <span
                                  className={`text-xs font-semibold tabular-nums ${toneTextClass(semaforoCumplimiento(pct))}`}
                                >
                                  {pct}%
                                </span>
                              ) : (
                                <span className="text-xs text-fg-tertiary">—</span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const parosTotal = g.tramos.reduce(
                                (acc, t) => acc + (t.paros ?? []).length,
                                0,
                              );
                              return parosTotal > 0 ? (
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-warn-soft px-1.5 py-0.5 text-xs font-medium text-warn">
                                  {parosTotal}
                                </span>
                              ) : (
                                <span className="text-xs text-fg-tertiary">—</span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right" />
                        </tr>
                        {g.tramos.map((t) => (
                          <tr
                            key={t.id}
                            className="cursor-pointer border-t border-border/60 text-xs transition-colors hover:bg-surface-overlay"
                            onClick={() => setDetalleHora(t)}
                          >
                            <td className="py-1.5 pl-6 pr-3 text-fg-tertiary">
                              {String(t.hora_inicio_bloque).slice(0, 5)}–
                              {String(t.hora_fin_bloque).slice(0, 5)} ·{' '}
                              {skus.find((s) => s.id === t.sku_id)?.codigo_cliente ?? 'SKU'}
                              <FuenteBadge
                                fuente={t.fuente}
                                pendienteCompletar={t.pendiente_completar}
                                className="ml-2 align-middle"
                              />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <CantidadConMetaCell registro={t} unidad={unidadDeSkuId(t.sku_id)} />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <CumplimientoBloqueCell registro={t} />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              {(t.paros ?? []).length > 0 ? (
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-warn-soft px-1.5 py-0.5 text-xs font-medium text-warn">
                                  {(t.paros ?? []).length}
                                </span>
                              ) : (
                                <span className="text-xs text-fg-tertiary">—</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <RegistroAccionesLider registro={t} currentUserId={currentUserId} />
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">Todavía no registraste nada en este turno.</p>
          )}
        </Collapsible>

        {/* Cumplimiento del día */}
        <Collapsible label="% cumplimiento del día (estimado)">
          {cumplimientoPct !== null ? (
            <p className="text-sm">
              <strong>{cumplimientoPct}%</strong> respecto a la capacidad estándar.
              <span className="block text-xs text-fg-tertiary">
                Ponderado por la capacidad estándar de cada SKU corrido en el turno. El dashboard
                del coordinador trae el cálculo real.
              </span>
            </p>
          ) : misHoras.data && misHoras.data.length > 0 ? (
            <p className="text-sm text-fg-tertiary">
              Los SKUs capturados no tienen capacidad estándar configurada — no se puede estimar.
            </p>
          ) : (
            <p className="text-sm text-fg-tertiary">Aún sin capturas en este turno.</p>
          )}
        </Collapsible>

        {/* Modal de paro (agregar nuevo paro al bloque actual) */}
        <ParoModal
          open={paroModalOpen}
          onClose={() => setParoModalOpen(false)}
          causales={causales}
          onAdd={(p) => {
            appendParo(p);
            setParoModalOpen(false);
          }}
        />

        {/* Modal "Agregar SKU" (multi-sku-por-hora, Lote 4): parte la celda
            agregando un tramo nuevo al final de lo ya cubierto. */}
        <AgregarTramoModal
          open={agregarTramoOpen}
          onClose={() => setAgregarTramoOpen(false)}
          skus={skus}
          celdaInicio={bloqueActivo}
          celdaFin={horaFinActiva}
          desdeInicial={desdeModal}
          tramosExistentes={tramosExtra}
          onAdd={(t) => {
            appendTramoExtra(t);
            setAgregarTramoOpen(false);
          }}
        />

        {/* Mini modal: cerrar paro abierto del turno (heredado) */}
        {/* Confirmación de producción 0 — doble seguro (paro total ya validado) */}
        <Dialog open={confirmarCero !== null} onClose={() => setConfirmarCero(null)}>
          <DialogHeader>
            <DialogTitle>¿Confirma que la producción fue 0?</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4">
            <p className="text-sm text-fg-secondary">
              El bloque{' '}
              <strong className="text-fg">
                {confirmarCero?.celda.inicio}–{confirmarCero?.celda.fin}
              </strong>{' '}
              quedará registrado con <strong className="text-fg">0 unidades</strong>, respaldado por
              los paros que cubren la hora completa. El supervisor lo verá para validar.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmarCero(null)}>
                Volver
              </Button>
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  if (!confirmarCero) return;
                  void onSubmit(confirmarCero.payload, confirmarCero.opts);
                  setConfirmarCero(null);
                }}
              >
                Sí, fue 0
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <CerrarParoDialog
          paro={paroACerrar}
          horaServidor={horaServidor}
          fechaHoy={fechaHoy}
          turno={programacion.turno}
          onClose={() => setParoACerrar(null)}
        />

        {/* Drill-down: detalle de una hora capturada */}
        <DetalleHoraDialog
          registro={detalleHora}
          causales={causales}
          skus={skus}
          onClose={() => setDetalleHora(null)}
        />
      </div>
    </LiderLayout>
  );
}

// =============================================================================
// BannerParosAbiertos — paros del turno sin hora_fin, heredados del bloque
// anterior. El líder los CIERRA acá, no los agrega como nuevos. Los que abrió
// la máquina (ref_externa) los cierra la máquina: se listan sin «Cerrar».
// =============================================================================

export function BannerParosAbiertos({
  paros,
  onCerrar,
}: {
  paros: ParoAbierto[];
  onCerrar: (paro: ParoAbierto) => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded border border-warn bg-warn-soft p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-warn">
        <span>⚠</span>
        <span>
          {paros.length === 1
            ? 'Tiene un paro sin cerrar del turno'
            : `Tiene ${paros.length} paros sin cerrar del turno`}
        </span>
      </div>
      <ul className="flex flex-col gap-2">
        {paros.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 rounded border border-border bg-surface-elevated px-3 py-2 text-sm"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-fg">
                {p.causal_madre_nombre} → {p.causal_nombre}
              </span>
              <span className="text-xs text-fg-tertiary">
                Bloque {p.bloque_hora_inicio}–{p.bloque_hora_fin} · empezó{' '}
                {new Date(p.hora_inicio).toLocaleTimeString('es-CO', {
                  timeZone: 'UTC' /* fake UTC: hora local etiquetada Z */,
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                · sigue activo
              </span>
            </div>
            {p.ref_externa ? (
              <span className="shrink-0 text-xs text-fg-tertiary">Lo cierra la máquina</span>
            ) : (
              <Button type="button" size="sm" onClick={() => onCerrar(p)}>
                Cerrar
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// BannerParosCerradosPorMtto — mantenimiento cerró un paro que el líder dejó
// abierto. Informativo, sin acción: el dato ya es de mantenimiento.
//
// Existe porque antes el paro simplemente DESAPARECÍA del banner de arriba y el
// líder nunca sabía con qué hora había quedado su paro (decisión del
// responsable 2026-08-14). Se descarta en memoria — al recargar vuelve, y se va
// solo cuando cambia el turno.
// =============================================================================

function BannerParosCerradosPorMtto({
  paros,
  onDescartar,
}: {
  paros: ParoCerradoPorMtto[];
  onDescartar: () => void;
}) {
  return (
    // Ojo: NO usar `bg-info-soft` — ese token no existe en tokens.css (solo hay
    // accent/ok/warn/crit -soft) y Tailwind no falla ante clases inexistentes:
    // el panel quedaría transparente en silencio (mismo bug que `bg-surface-raised`).
    <div className="flex flex-col gap-2 rounded border border-info bg-surface-elevated p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-info">
          <span>🔧</span>
          <span>
            {paros.length === 1
              ? 'Mantenimiento cerró un paro suyo'
              : `Mantenimiento cerró ${paros.length} paros suyos`}
          </span>
        </div>
        <button
          type="button"
          onClick={onDescartar}
          className="shrink-0 text-xs text-fg-tertiary underline underline-offset-2 hover:text-fg"
        >
          Entendido
        </button>
      </div>
      <ul className="flex flex-col gap-2">
        {paros.map((p) => (
          <li key={p.id} className="rounded border border-border bg-surface-base px-3 py-2 text-sm">
            <span className="block truncate font-medium text-fg">
              {p.causal_madre_nombre} → {p.causal_nombre}
            </span>
            <span className="text-xs text-fg-tertiary">
              Bloque {p.bloque_hora_inicio}–{p.bloque_hora_fin} · cerrado a las{' '}
              {new Date(p.hora_fin).toLocaleTimeString('es-CO', {
                timeZone: 'UTC' /* fake UTC: hora local etiquetada Z */,
                hour: '2-digit',
                minute: '2-digit',
              })}
              {p.minutos_perdidos === null ? '' : ` · ${Math.round(p.minutos_perdidos)} min`} · por{' '}
              {p.cerrado_por.nombre} {p.cerrado_por.apellido}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// CerrarParoDialog — mini modal para cerrar un paro abierto del turno
// =============================================================================

function CerrarParoDialog({
  paro,
  horaServidor,
  fechaHoy,
  turno,
  onClose,
}: {
  paro: ParoAbierto | null;
  horaServidor: string; // "HH:MM"
  fechaHoy: string; // "YYYY-MM-DD"
  turno: { hora_inicio: string; cruza_medianoche: boolean };
  onClose: () => void;
}) {
  const { atribucionCruce } = useTenantConfig();
  const cerrarMut = useCerrarParo();
  const [horaFin, setHoraFin] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // Cuando se abre, sugerir hora actual del servidor como hora_fin default
  useEffect(() => {
    if (paro) {
      setHoraFin(horaServidor);
      setObservaciones('');
    }
  }, [paro, horaServidor]);

  const open = paro !== null;

  async function handleConfirm() {
    if (!paro) return;
    if (!horaFin) {
      toast.error('Indique una hora de cierre');
      return;
    }
    try {
      // Cierre medianoche-aware: si la HH:MM compuesta queda antes del inicio
      // del paro, el cierre cayó del otro lado de la medianoche → +1 día
      // (paro del T3 abierto 23:40, cerrado 00:20). Si queda igual, se
      // rechaza abajo con mensaje claro.
      const horaFinIso = combinarHoraCierreParo(
        fechaHoy,
        horaFin,
        paro.hora_inicio,
        turno,
        atribucionCruce,
      );
      if (new Date(horaFinIso) <= new Date(paro.hora_inicio)) {
        toast.error('La hora de cierre debe ser posterior al inicio del paro');
        return;
      }
      // Plausibilidad: el salto de medianoche puede convertir un error de
      // tipeo (cerrar "13:00" un paro de las 14:10) en un paro de ~23h.
      // Ningún paro real supera la ventana de un turno — tope 12h.
      const duracionHoras =
        (new Date(horaFinIso).getTime() - new Date(paro.hora_inicio).getTime()) / 3_600_000;
      if (duracionHoras > 12) {
        toast.error('Hora de cierre inválida', {
          description: `El paro quedaría de ${Math.round(duracionHoras)} horas. Revise la hora ingresada.`,
        });
        return;
      }
      await cerrarMut.mutateAsync({
        paroId: paro.id,
        hora_fin: horaFinIso,
        observaciones: observaciones.trim() || null,
      });
      toast.success('Paro cerrado');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo cerrar el paro';
      toast.error('Error al cerrar', { description: msg });
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Cerrar paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        {paro ? (
          <>
            <div className="rounded bg-surface-overlay p-2 text-xs text-fg-secondary">
              <strong>
                {paro.causal_madre_nombre} → {paro.causal_nombre}
              </strong>
              <br />
              Empezó a las{' '}
              {new Date(paro.hora_inicio).toLocaleTimeString('es-CO', {
                timeZone: 'UTC' /* fake UTC: hora local etiquetada Z */,
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              en el bloque {paro.bloque_hora_inicio}–{paro.bloque_hora_fin}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cerrar-hora-fin">Hora de cierre</Label>
              <Input
                id="cerrar-hora-fin"
                type="time"
                value={horaFin}
                onChange={(e) => setHoraFin(e.target.value)}
              />
              <p className="text-xs text-fg-tertiary">
                Pre-cargada con la hora actual del servidor ({horaServidor}). Edite si fue antes.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cerrar-obs">Observaciones (opcional)</Label>
              <Textarea
                id="cerrar-obs"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Ej. resuelto por mantenimiento, sin más detalles"
              />
            </div>
          </>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={cerrarMut.isPending}>
          Cancelar
        </Button>
        <Button onClick={handleConfirm} disabled={cerrarMut.isPending}>
          {cerrarMut.isPending ? (
            <>
              <Spinner className="border-white/30 border-t-white" /> Cerrando…
            </>
          ) : (
            'Cerrar paro'
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// =============================================================================
// DetalleHoraDialog — drill-down de "mis horas" con todos los campos
// =============================================================================

export function DetalleHoraDialog({
  registro,
  causales,
  skus,
  onClose,
}: {
  registro: RegistroProduccionHora | null;
  causales: CausalParoTreeNode[];
  skus: SkuConCapacidad[];
  onClose: () => void;
}) {
  const open = registro !== null;
  if (!registro) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogHeader>
          <DialogTitle>Detalle</DialogTitle>
        </DialogHeader>
        <DialogContent />
      </Dialog>
    );
  }
  const sku = skus.find((s) => s.id === registro.sku_id);
  const horaIni = String(registro.hora_inicio_bloque).slice(0, 5);
  const horaFin = String(registro.hora_fin_bloque).slice(0, 5);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>
          Bloque {horaIni}–{horaFin}
        </DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3 text-sm">
        <DetalleFila label="SKU">
          {sku ? `${sku.codigo_cliente} · ${sku.descripcion}` : registro.sku_id.slice(0, 8)}
        </DetalleFila>
        <DetalleFila label="Cantidad procesada">
          <span className="tabular-nums">{formatearMiles(registro.cantidad_procesada)}</span>
        </DetalleFila>
        <DetalleFila label="Meta de la hora">
          {registro.capacidad_estandar_unidades_hora ? (
            <span className="tabular-nums">
              {formatearMiles(registro.capacidad_estandar_unidades_hora)}
            </span>
          ) : (
            '—'
          )}
        </DetalleFila>
        <DetalleFila label="Cumplimiento">
          <CumplimientoBloqueCell registro={registro} />
        </DetalleFila>
        <DetalleFila label="Operarios">{registro.numero_operarios}</DetalleFila>
        <DetalleFila label="Golpes/min">
          {registro.golpes_por_minuto !== null ? registro.golpes_por_minuto : '—'}
        </DetalleFila>
        <DetalleFila label="OP / Lote">{registro.op_lote_oc_cliente ?? '—'}</DetalleFila>
        <DetalleFila label="Estado">
          {registro.validado_at ? (
            <span className="text-ok">Validado</span>
          ) : (
            <span className="text-fg-tertiary">Pendiente de validación</span>
          )}
        </DetalleFila>
        {registro.paros && registro.paros.length > 0 ? (
          <div className="mt-1 flex flex-col gap-2 border-t border-border pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-fg-tertiary">
              Paros ({registro.paros.length})
            </p>
            {registro.paros.map((p) => {
              const causalNombre = findCausalNombre(causales, p.causal_id) ?? 'Sin causal';
              const inicio = new Date(p.hora_inicio).toLocaleTimeString('es-CO', {
                timeZone: 'UTC' /* fake UTC: hora local etiquetada Z */,
                hour: '2-digit',
                minute: '2-digit',
              });
              const fin = p.hora_fin
                ? new Date(p.hora_fin).toLocaleTimeString('es-CO', {
                    timeZone: 'UTC' /* fake UTC: hora local etiquetada Z */,
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'abierto';
              return (
                <div key={p.id} className="rounded border border-border p-2 text-xs">
                  <p className="font-medium text-fg">{causalNombre}</p>
                  <p className="text-fg-tertiary">
                    {inicio} → {fin}
                    {p.minutos_perdidos !== null ? ` · ${Math.round(p.minutos_perdidos)} min` : ''}
                  </p>
                  {p.observaciones ? (
                    <p className="mt-1 italic text-fg-secondary">{p.observaciones}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cerrar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function DetalleFila({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-fg-tertiary">{label}</span>
      <span className="text-right text-fg">{children}</span>
    </div>
  );
}

// =============================================================================
// ParoModal — agrega un paro al field array del form padre
// =============================================================================

interface ParoModalProps {
  open: boolean;
  onClose: () => void;
  causales: CausalParoTreeNode[];
  onAdd: (paro: FormParo) => void;
}

function ParoModal({ open, onClose, causales, onAdd }: ParoModalProps) {
  const [causalMadreId, setCausalMadreId] = useState('');
  const [causalId, setCausalId] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFin, setHoraFin] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const causalSeleccionada = causalEspecificaDe(causales, causalMadreId, causalId);
  const requiereObs = causalSeleccionada?.requiere_observacion ?? false;

  function handleAdd() {
    setError(null);
    const result = formParoSchema.safeParse({
      causal_madre_id: causalMadreId,
      causal_id: causalId,
      hora_inicio: horaInicio,
      hora_fin: horaFin || null,
      observaciones: observaciones.trim() || null,
      requiere_observacion: requiereObs,
    });
    if (!result.success) {
      const firstErr = result.error.errors[0];
      setError(firstErr ? `${firstErr.path.join('.')}: ${firstErr.message}` : 'Datos inválidos');
      return;
    }
    if (requiereObs && (!observaciones || observaciones.trim().length < 5)) {
      setError('Observación obligatoria (mínimo 5 caracteres) para esta causal');
      return;
    }
    if (horaFin && horaFin <= horaInicio) {
      setError('Hora fin debe ser posterior a hora inicio');
      return;
    }
    onAdd(result.data);
    // Reset
    setCausalMadreId('');
    setCausalId('');
    setHoraInicio('');
    setHoraFin('');
    setObservaciones('');
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Agregar paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <CausalPicker
          causales={causales}
          causalMadreId={causalMadreId}
          causalId={causalId}
          onCausalMadreChange={setCausalMadreId}
          onCausalChange={setCausalId}
        />

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="paro-inicio">Hora inicio</Label>
            <Input
              id="paro-inicio"
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="paro-fin">Hora fin (opcional)</Label>
            <Input
              id="paro-fin"
              type="time"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="paro-obs">Observaciones{requiereObs ? ' (obligatorio)' : ''}</Label>
          <Textarea
            id="paro-obs"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Detalle del paro"
          />
        </div>

        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleAdd}>Agregar</Button>
      </DialogFooter>
    </Dialog>
  );
}

// =============================================================================
// AgregarTramoModal — agrega un tramo (2º+ SKU) al field array `tramosExtra`
// del form padre (multi-sku-por-hora, Lote 4). Calca la estructura de
// ParoModal: estado local + validación al presionar "Agregar", el error se ve
// ahí (no recién al Guardar el bloque completo).
// =============================================================================

interface AgregarTramoModalProps {
  open: boolean;
  onClose: () => void;
  skus: SkuConCapacidad[];
  celdaInicio: string;
  celdaFin: string;
  /** Prefill de "Desde": hasta del último tramo actual (Req. UI, escenario
   *  "Agregar tramo vía modal"). */
  desdeInicial: string;
  tramosExistentes: { hora_inicio_bloque: string; hora_fin_bloque: string }[];
  onAdd: (tramo: RegistroTramoCreate) => void;
}

function AgregarTramoModal({
  open,
  onClose,
  skus,
  celdaInicio,
  celdaFin,
  desdeInicial,
  tramosExistentes,
  onAdd,
}: AgregarTramoModalProps) {
  const [desde, setDesde] = useState(desdeInicial);
  const [hasta, setHasta] = useState(celdaFin);
  const [skuId, setSkuId] = useState('');
  const [cantidad, setCantidad] = useState<number | null>(null);
  const [operarios, setOperarios] = useState<number | null>(null);
  const [golpes, setGolpes] = useState<number | null>(null);
  const [opLote, setOpLote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { formatoLote } = useTenantConfig();
  const inputLote = propsInputLote(formatoLote);

  const selectedSku = skus.find((s) => s.id === skuId) ?? null;

  useEffect(() => {
    if (selectedSku && !selectedSku.golpes_min_aplica) setGolpes(null);
  }, [selectedSku]);

  // Al abrir, re-prellenar Desde/Hasta con los valores vigentes (el último
  // tramo pudo haber cambiado desde la última apertura) y limpiar el resto.
  useEffect(() => {
    if (open) {
      setDesde(desdeInicial);
      setHasta(celdaFin);
      setSkuId('');
      setCantidad(null);
      setOperarios(null);
      setGolpes(null);
      setOpLote('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [open]);

  function handleAdd() {
    setError(null);
    const partitionErr = validarNuevoTramo({
      desde,
      hasta,
      celdaInicio,
      celdaFin,
      tramosExistentes,
    });
    if (partitionErr) {
      setError(partitionErr);
      return;
    }
    const result = registroTramoCreateSchema.safeParse({
      hora_inicio_bloque: desde,
      hora_fin_bloque: hasta,
      sku_id: skuId,
      cantidad_procesada: cantidad,
      cantidad_kg: null,
      golpes_por_minuto: golpes,
      numero_operarios: operarios,
      op_lote_oc_cliente: opLote,
    });
    if (!result.success) {
      const firstErr = result.error.errors[0];
      setError(firstErr ? firstErr.message : 'Datos inválidos');
      return;
    }
    // El schema solo valida la forma genérica; el formato es de la empresa (B7).
    const loteOk = validarLoteUi(result.data.op_lote_oc_cliente, formatoLote);
    if (loteOk !== true) {
      setError(loteOk);
      return;
    }
    onAdd(result.data);
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Agregar SKU</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tramo-desde">Desde</Label>
            <Input
              id="tramo-desde"
              type="time"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tramo-hasta">Hasta</Label>
            <Input
              id="tramo-hasta"
              type="time"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tramo-sku">SKU del tramo</Label>
          <Combobox
            id="tramo-sku"
            options={skus.map((s) => ({
              value: s.id,
              label: s.codigo_cliente,
              description: `${s.descripcion}${s.formato ? ` · ${s.formato}` : ''}`,
              searchText: `${s.codigo_cliente} ${s.descripcion} ${s.formato ?? ''}`,
            }))}
            value={skuId}
            onChange={setSkuId}
            placeholder="Buscar por código o descripción…"
            emptyLabel="Sin coincidencias"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CantidadInput
            id="tramo-cantidad"
            label="Cantidad del tramo"
            placeholder="Ej. 620"
            value={cantidad}
            onChange={setCantidad}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tramo-operarios">Operarios del tramo (1–20)</Label>
            <Input
              id="tramo-operarios"
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              step={1}
              placeholder="Ej. 3"
              value={operarios ?? ''}
              onChange={(e) => setOperarios(e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tramo-golpes">
            Golpes/min del tramo
            {selectedSku && !selectedSku.golpes_min_aplica ? ' (no aplica a este SKU)' : ''}
          </Label>
          <Input
            id="tramo-golpes"
            type="number"
            inputMode="decimal"
            min={1}
            step={0.1}
            disabled={Boolean(selectedSku && !selectedSku.golpes_min_aplica) || cantidad === 0}
            placeholder={selectedSku && !selectedSku.golpes_min_aplica ? 'No aplica' : 'Ej. 80'}
            value={golpes ?? ''}
            onChange={(e) => setGolpes(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tramo-op">OP / Lote del tramo</Label>
          <Input
            id="tramo-op"
            type="text"
            inputMode={inputLote.inputMode}
            maxLength={inputLote.maxLength}
            placeholder={inputLote.placeholder}
            value={opLote}
            onChange={(e) => setOpLote(limpiarEntradaLote(e.target.value, formatoLote))}
          />
        </div>

        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleAdd}>Agregar</Button>
      </DialogFooter>
    </Dialog>
  );
}

// =============================================================================
// BarraProgresoTurno — % de tiempo transcurrido en el turno + label de avance
// =============================================================================

function BarraProgresoTurno({
  turno,
  now,
  horasCapturadas,
  totalHorasTurno,
  horasPendientes,
}: {
  turno: { hora_inicio: string; hora_fin: string; cruza_medianoche: boolean };
  now: Date;
  horasCapturadas: number;
  totalHorasTurno: number;
  horasPendientes: number;
}) {
  const totalMin = minutosEnTurno(turno);
  const trascurridoMin = minutosTrascurridosDesdeInicio(turno, now);
  const pct = totalMin > 0 ? Math.min(100, Math.max(0, (trascurridoMin / totalMin) * 100)) : 0;
  const pendientesLabel =
    horasPendientes > 0
      ? `${formatHorasCantidad(horasPendientes)} pendiente${horasPendientes === 1 ? '' : 's'}`
      : 'al día';
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[11px] text-fg-tertiary">
        <span>
          {formatHorasCantidad(horasCapturadas)} de {formatHorasCantidad(totalHorasTurno)}{' '}
          {totalHorasTurno === 1 ? 'hora' : 'horas'}
        </span>
        <span className={horasPendientes > 0 ? 'font-medium text-warn' : 'font-medium text-ok'}>
          {pendientesLabel}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded bg-surface-overlay">
        <div
          className="h-full bg-accent transition-all"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

// =============================================================================
// formatearFechaCorta — "Jue 26 de may." en es-CO desde una fecha YYYY-MM-DD
// =============================================================================

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  // Construir Date desde "YYYY-MM-DD" usando UTC para no shiftear el día por la
  // zona del browser. Después formateamos en es-CO sin tz (los componentes
  // son year/month/day, no hay tiempo en juego acá).
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  // "lun., 26 may." → "Lun 26 may"
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Formatea una cantidad de horas (puede tener decimales por bloques
 * parciales de borde de turno) para el header contextual: enteros sin
 * decimales ("3 horas"), fraccionarios a 1 decimal ("0.5 horas"). Con turnos
 * de bloques :00 puros el resultado es siempre entero — no-regresión con el
 * conteo previo.
 */
function formatHorasCantidad(n: number): string {
  const redondeado = Math.round(n * 10) / 10;
  return Number.isInteger(redondeado) ? String(redondeado) : redondeado.toFixed(1);
}

function minutosEnTurno(turno: {
  hora_inicio: string;
  hora_fin: string;
  cruza_medianoche: boolean;
}): number {
  const toMin = (h: string) => {
    const [hh, mm] = h.split(':');
    return Number(hh) * 60 + Number(mm);
  };
  const ini = toMin(turno.hora_inicio);
  const fin = toMin(turno.hora_fin);
  return turno.cruza_medianoche ? 24 * 60 - ini + fin : fin - ini;
}

function minutosTrascurridosDesdeInicio(
  turno: { hora_inicio: string; cruza_medianoche: boolean },
  now: Date,
): number {
  const [hh, mm] = turno.hora_inicio.split(':');
  const iniMin = Number(hh) * 60 + Number(mm);
  const curMin = now.getHours() * 60 + now.getMinutes();
  if (turno.cruza_medianoche && curMin < iniMin) {
    return 24 * 60 - iniMin + curMin;
  }
  return curMin - iniMin;
}

/**
 * Combina una fecha del servidor (YYYY-MM-DD zona tenant) con una hora HH:MM
 * local para producir un timestamp ISO. Usamos los componentes year/month/day
 * directamente (sin Date constructor) para evitar shifts por zona del browser:
 * el resultado se manda al backend que ya sabe interpretar la fecha del tenant.
 *
 * Fix: antes se usaba `Date` del browser (`now`) que en otra TZ daba paros
 * con timestamp del momento de submit, no del bloque que el líder estaba
 * capturando.
 */
/**
 * "Ahora" del servidor en ms, derivado de fecha_hoy + hora_servidor (zona del
 * tenant) tratados como wall-clock UTC. Se usa SOLO para gates UX (ventana de
 * 30 min); el server re-valida y devuelve 423 si hay skew.
 */
export function serverNowMsFrom(fechaHoy: string, horaServidorHHMM: string): number {
  const [y, m, d] = fechaHoy.split('-').map(Number);
  const [hh, mm] = horaServidorHHMM.split(':').map(Number);
  if (!y || !m || !d || hh === undefined || mm === undefined) return Date.now();
  return Date.UTC(y, m - 1, d, hh, mm, 0);
}

// (combinarFechaHora se movió a schemas: combinarFechaHoraTurno y
// combinarHoraCierreParo, versiones medianoche-aware para turnos cruza.)

function findCausalNombre(tree: CausalParoTreeNode[], causalId: string): string | null {
  for (const madre of tree) {
    if (madre.id === causalId) return madre.nombre;
    const h = madre.hijas.find((x) => x.id === causalId);
    if (h) return `${madre.nombre} → ${h.nombre}`;
  }
  return null;
}

function ErrorState({ message, onSignOut }: { message: string; onSignOut: () => Promise<void> }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-medium text-crit">{message}</h1>
      <button onClick={onSignOut} className="text-xs text-fg-tertiary underline">
        Cerrar sesión
      </button>
    </main>
  );
}
